import { useState, useEffect, useCallback, useRef } from 'react';
import { defaultBlogPosts } from '@/mocks/blogPosts';
import {
  readCachedPosts,
  readPostsFromDb,
  writeCachedPosts,
  writePostsToDb,
} from '@/lib/blogStorage';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  coverImage?: string;
  content: string;
  author: string;
  category?: string;
  tags?: string[];
  status: 'draft' | 'published';
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

function normaliseTitle(title: string): string {
  return (title || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// Merge locally stored posts with the built-in article list so newly added
// default articles appear even in browsers holding an older cached list.
function mergeWithDefaults(stored: BlogPost[]): BlogPost[] {
  if (stored.length === 0) return defaultBlogPosts;

  const defaultBySlug = new Map(defaultBlogPosts.map((p) => [p.slug, p]));
  const defaultByTitle = new Map(defaultBlogPosts.map((p) => [normaliseTitle(p.title), p]));
  const matchedDefaultIds = new Set<string>();

  const merged = stored.map((p) => {
    const def = defaultBySlug.get(p.slug) ?? defaultByTitle.get(normaliseTitle(p.title));
    if (!def) return p;
    matchedDefaultIds.add(def.id);
    const storedTime = new Date(p.updatedAt || p.createdAt).getTime();
    const defaultTime = new Date(def.updatedAt || def.createdAt).getTime();
    // A built-in article that was updated after the stored copy always wins,
    // so the site's published version can replace an old local draft.
    return storedTime > defaultTime ? p : def;
  });

  const missingDefaults = defaultBlogPosts.filter((p) => !matchedDefaultIds.has(p.id));
  return missingDefaults.length > 0 ? [...merged, ...missingDefaults] : merged;
}

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>(() => mergeWithDefaults(readCachedPosts()));
  const [hydrated, setHydrated] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // If the user saves something before the database has finished loading, that
  // edit must win over the copy coming back from storage.
  const editedBeforeHydration = useRef(false);

  // Load the authoritative copy (IndexedDB) once on mount.
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        const stored = await readPostsFromDb();
        if (cancelled) return;
        if (editedBeforeHydration.current) {
          // Keep what the user just saved; it will be persisted below.
        } else if (stored) {
          setPosts(mergeWithDefaults(stored));
        } else {
          // First run in this browser: seed the database from the cache/defaults.
          const initial = mergeWithDefaults(readCachedPosts());
          await writePostsToDb(initial).catch(() => undefined);
          if (!cancelled) setPosts(initial);
        }
      } catch {
        // IndexedDB unavailable — the localStorage cache keeps the site working.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, []);

  // Persist every change, but only after hydration so an older cache can never
  // overwrite the full list that lives in the database.
  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;

    const persist = async () => {
      writeCachedPosts(posts);
      try {
        await writePostsToDb(posts);
        if (!cancelled) setSaveError(null);
      } catch {
        if (!cancelled) {
          setSaveError(
            'Your changes could not be saved in this browser. Please free up storage space and try again.',
          );
        }
      }
    };

    persist();

    return () => {
      cancelled = true;
    };
  }, [posts, hydrated]);

  const resetToDefaults = useCallback(() => {
    editedBeforeHydration.current = true;
    setPosts(defaultBlogPosts);
  }, []);

  const getPostBySlug = useCallback(
    (slug: string): BlogPost | undefined => posts.find((p) => p.slug === slug),
    [posts],
  );

  const getPostById = useCallback(
    (id: string): BlogPost | undefined => posts.find((p) => p.id === id),
    [posts],
  );

  const createPost = useCallback(
    (post: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>): BlogPost => {
      const now = new Date().toISOString();
      // Keep slugs unique so publishing several posts in quick succession
      // never produces two articles competing for the same URL.
      const taken = new Set(posts.map((p) => p.slug));
      let slug = post.slug || 'untitled-post';
      if (taken.has(slug)) {
        let n = 2;
        while (taken.has(`${slug}-${n}`)) n += 1;
        slug = `${slug}-${n}`;
      }
      const newPost: BlogPost = {
        ...post,
        slug,
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
        createdAt: now,
        updatedAt: now,
      };
      editedBeforeHydration.current = true;
      setPosts((prev) => [newPost, ...prev]);
      return newPost;
    },
    [posts],
  );

  const updatePost = useCallback(
    (id: string, updates: Partial<Omit<BlogPost, 'id' | 'createdAt'>>): BlogPost | null => {
      const existing = posts.find((p) => p.id === id);
      if (!existing) return null;
      const updated: BlogPost = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      editedBeforeHydration.current = true;
      setPosts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      return updated;
    },
    [posts],
  );

  const deletePost = useCallback((id: string): void => {
    editedBeforeHydration.current = true;
    setPosts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const publishedPosts = posts
    .filter((p) => p.status === 'published')
    .sort((a, b) => {
      const da = new Date(a.publishedAt || a.createdAt).getTime();
      const db = new Date(b.publishedAt || b.createdAt).getTime();
      return db - da; // newest first, based on the publish date
    });

  return {
    posts,
    publishedPosts,
    loading: !hydrated,
    hydrated,
    saveError,
    getPostBySlug,
    getPostById,
    createPost,
    updatePost,
    deletePost,
    resetToDefaults,
  };
}